const STORAGE_KEY = 'fatsale:selected-wallet'

const EMPTY_SNAPSHOT = {
    account: '',
    chainId: null,
    provider: null,
    walletId: '',
    walletName: '',
    wallets: [],
    status: 'idle',
    error: '',
}

const KNOWN_WALLETS = [
    { id: 'binance', name: 'Binance Wallet', icon: '/img/binance.png', matches: (provider) => provider?.isBinance || provider?.isBinanceChain },
    { id: 'okx', name: 'OKX Wallet', matches: (provider) => provider?.isOkxWallet || provider?.isOKExWallet },
    { id: 'coinbase', name: 'Coinbase Wallet', matches: (provider) => provider?.isCoinbaseWallet },
    { id: 'tokenpocket', name: 'TokenPocket', matches: (provider) => provider?.isTokenPocket },
    { id: 'metamask', name: 'MetaMask', icon: '/img/metamask.png', matches: (provider) => provider?.isMetaMask && !provider?.isBraveWallet },
    { id: 'brave', name: 'Brave Wallet', matches: (provider) => provider?.isBraveWallet },
]

function parseChainId(value) {
    if (typeof value === 'number') return value
    if (typeof value === 'bigint') return Number(value)
    if (typeof value !== 'string' || value.length === 0) return null
    return Number.parseInt(value, value.startsWith('0x') ? 16 : 10)
}

function safeIcon(icon, fallback = '') {
    if (typeof icon !== 'string') return fallback
    if (/^data:image\/(?:png|jpeg|jpg|webp|svg\+xml);/i.test(icon)) return icon
    if (/^https:\/\//i.test(icon)) return icon
    return fallback
}

function identifyProvider(provider, info = {}) {
    const known = KNOWN_WALLETS.find((wallet) => wallet.matches(provider))
    const rdns = typeof info.rdns === 'string' ? info.rdns.toLowerCase() : ''
    const rdnsKnown = KNOWN_WALLETS.find((wallet) => rdns.includes(wallet.id))
    const metadata = known || rdnsKnown
    const stableId = info.uuid || info.rdns || metadata?.id || 'browser-wallet'
    return {
        id: String(stableId).toLowerCase(),
        name: info.name || metadata?.name || 'Browser Wallet',
        icon: safeIcon(info.icon, metadata?.icon || ''),
        rdns: info.rdns || '',
        provider,
    }
}

function uniqueId(baseId, wallets) {
    const walletIds = new Set(wallets.map((wallet) => wallet.id))
    if (!walletIds.has(baseId)) return baseId
    let suffix = 2
    while (walletIds.has(`${baseId}-${suffix}`)) suffix += 1
    return `${baseId}-${suffix}`
}

export function createWalletProviderManager({
    windowRef = typeof window !== 'undefined' ? window : null,
    settleMs = 120,
} = {}) {
    let snapshot = { ...EMPTY_SNAPSHOT }
    let wallets = []
    let activeProvider = null
    let activeHandlers = null
    let discoveryStarted = false
    let restorePromise = null
    const subscribers = new Set()

    const publish = (patch = {}) => {
        snapshot = { ...snapshot, ...patch, wallets: [...wallets] }
        subscribers.forEach((listener) => listener({ ...snapshot }))
        return { ...snapshot }
    }

    const addWallet = (provider, info = {}) => {
        if (!provider) return
        const existingIndex = wallets.findIndex((wallet) => wallet.provider === provider)
        if (existingIndex >= 0) {
            const identified = identifyProvider(provider, info)
            wallets = wallets.map((wallet, index) => index === existingIndex ? {
                ...wallet,
                name: info.name || wallet.name,
                icon: identified.icon || wallet.icon,
                rdns: info.rdns || wallet.rdns,
            } : wallet)
            publish()
            return
        }
        const wallet = identifyProvider(provider, info)
        wallet.id = uniqueId(wallet.id, wallets)
        wallets = [...wallets, wallet]
        publish()
    }

    const handleAnnouncement = (event) => {
        if (!event?.detail?.provider) return
        addWallet(event.detail.provider, event.detail.info || {})
    }

    const startDiscovery = () => {
        if (!windowRef || discoveryStarted) return
        discoveryStarted = true
        windowRef.addEventListener?.('eip6963:announceProvider', handleAnnouncement)
        const ethereum = windowRef.ethereum
        const legacyProviders = Array.isArray(ethereum?.providers) ? ethereum.providers : (ethereum ? [ethereum] : [])
        legacyProviders.forEach((provider) => addWallet(provider))
        windowRef.dispatchEvent?.(new Event('eip6963:requestProvider'))
    }

    const discoverWalletProviders = async () => {
        startDiscovery()
        publish({ status: snapshot.account ? 'connected' : 'discovering', error: '' })
        if (settleMs > 0) await new Promise((resolve) => setTimeout(resolve, settleMs))
        return publish({ status: snapshot.account ? 'connected' : 'idle' })
    }

    const removeActiveListeners = () => {
        if (!activeHandlers) return
        if (activeProvider?.removeListener) {
            Object.entries(activeHandlers).forEach(([event, handler]) => activeProvider.removeListener(event, handler))
        }
        activeHandlers = null
    }

    const clearActiveWallet = () => {
        removeActiveListeners()
        activeProvider = null
        windowRef?.localStorage?.removeItem(STORAGE_KEY)
        return publish({ ...EMPTY_SNAPSHOT, wallets: [...wallets] })
    }

    const attachActiveListeners = (provider, wallet) => {
        removeActiveListeners()
        activeProvider = provider
        if (!provider?.on) return
        activeHandlers = {
            accountsChanged: (accounts = []) => {
                if (activeProvider !== provider) return
                const account = accounts[0] || ''
                publish({ account, status: account ? 'connected' : 'idle', error: '' })
            },
            chainChanged: (chainId) => {
                if (activeProvider !== provider) return
                publish({ chainId: parseChainId(chainId), error: '' })
            },
            disconnect: () => {
                if (activeProvider === provider) clearActiveWallet()
            },
        }
        Object.entries(activeHandlers).forEach(([event, handler]) => provider.on(event, handler))
        publish({ walletId: wallet.id, walletName: wallet.name })
    }

    const connectWallet = async (walletId) => {
        if (!discoveryStarted) await discoverWalletProviders()
        const wallet = wallets.find((item) => item.id === walletId)
        if (!wallet) throw new Error('Wallet provider is no longer available')
        publish({ status: 'connecting', error: '', walletId: wallet.id, walletName: wallet.name })
        try {
            const [accounts, chainId] = await Promise.all([
                wallet.provider.request({ method: 'eth_requestAccounts' }),
                wallet.provider.request({ method: 'eth_chainId' }),
            ])
            const account = accounts?.[0] || ''
            if (!account) throw new Error('Wallet did not return an account')
            windowRef?.localStorage?.setItem(STORAGE_KEY, wallet.id)
            attachActiveListeners(wallet.provider, wallet)
            return publish({ account, chainId: parseChainId(chainId), provider: wallet.provider, status: 'connected', error: '' })
        } catch (error) {
            return Promise.reject(Object.assign(error, {
                walletSnapshot: publish({ status: 'error', error: error?.message || String(error) }),
            }))
        }
    }

    const performWalletRestore = async () => {
        await discoverWalletProviders()
        const selectedId = windowRef?.localStorage?.getItem(STORAGE_KEY)
        if (!selectedId) return publish({ status: 'idle' })
        const wallet = wallets.find((item) => item.id === selectedId)
        if (!wallet) return clearActiveWallet()
        try {
            const [accounts, chainId] = await Promise.all([
                wallet.provider.request({ method: 'eth_accounts' }),
                wallet.provider.request({ method: 'eth_chainId' }),
            ])
            if (!accounts?.[0]) return publish({ walletId: wallet.id, walletName: wallet.name, status: 'idle' })
            attachActiveListeners(wallet.provider, wallet)
            return publish({ account: accounts[0], chainId: parseChainId(chainId), provider: wallet.provider, status: 'connected', error: '' })
        } catch (error) {
            return publish({ status: 'idle', error: error?.message || String(error) })
        }
    }

    const restoreWallet = () => {
        if (restorePromise) return restorePromise
        restorePromise = performWalletRestore().finally(() => {
            restorePromise = null
        })
        return restorePromise
    }

    const disconnectWallet = () => {
        return clearActiveWallet()
    }

    const subscribeWallet = (listener) => {
        subscribers.add(listener)
        listener({ ...snapshot, wallets: [...wallets] })
        return () => subscribers.delete(listener)
    }

    const destroy = () => {
        removeActiveListeners()
        windowRef?.removeEventListener?.('eip6963:announceProvider', handleAnnouncement)
        subscribers.clear()
    }

    return { connectWallet, destroy, disconnectWallet, discoverWalletProviders, getWalletSnapshot: () => ({ ...snapshot, wallets: [...wallets] }), restoreWallet, subscribeWallet }
}

let browserManager
function getBrowserManager() {
    if (!browserManager) browserManager = createWalletProviderManager()
    return browserManager
}

export const connectWallet = (walletId) => getBrowserManager().connectWallet(walletId)
export const disconnectWallet = () => getBrowserManager().disconnectWallet()
export const discoverWalletProviders = () => getBrowserManager().discoverWalletProviders()
export const getWalletSnapshot = () => getBrowserManager().getWalletSnapshot()
export const restoreWallet = () => getBrowserManager().restoreWallet()
export const subscribeWallet = (listener) => getBrowserManager().subscribeWallet(listener)
